// Extensions are the places where things can be extended.
// The name of an extension is defined by the place where it should be defined.
// This means we would have a registration for a particular point, which refers to a component.
// The Extension component and useExtension hook would be used where you want something to appear
// we also have a repeatable extension point (like for tabs)

//TODO: rename this file to not imply that they're all hooks

import {
	componentExtensionTopic,
	extensionContextStore,
	extensionTopic,
	getExtension,
	getExtensions,
	useExtensionContext
} from './extension-context';

import { ComponentType, useContext, useEffect, useMemo, useState } from 'react';
import { useAccessFlags } from './feature-context';
import { IdentityContext } from './identity-context';
import { PageNotFound } from './page-not-found';
import { ChildOnlyOption, IRoute } from './types';
import { useIdentity } from './use-identity';

// component extensions
export const useComponentExtension = <TName extends string = string>(name: TName, allowUnauthorized?: boolean) => {
	return useComponentExtensions(name, allowUnauthorized)?.[0] ?? null;
};

export function useExtension<T extends {}>(name: string): T {
	return useExtensions<T>(name)?.[0] ?? null;
}

/** Client side message system that allows a message being sent to a topic to force any components with that topic to re-render. */
export function useSubscribeTopic(topic: string) {
	const { subscribeUpdates } = useExtensionContext();

	const [, setForceRefresh] = useState(false);

	useEffect(() => {
		return subscribeUpdates(topic, () => {
			setForceRefresh(r => !r);
		});
	}, [topic]);
}
/**subscribe to multiple topics */
export function useSubscribeTopicsWithHandler(topics: string[] | undefined, handler: Function) {
	const { subscribeUpdatesArray } = useExtensionContext();

	const memoizedTopics = useMemo(() => topics, [topics]);

	useEffect(() => {
		if (!memoizedTopics) return;
		return subscribeUpdatesArray(memoizedTopics, (event: CustomEvent<unknown>) => {
			handler(event);
		});
	}, [memoizedTopics]);
}

export function useSubscribeTopicWithHandler(topic: string | undefined, handler: Function) {
	const { subscribeUpdates } = useExtensionContext();

	useEffect(() => {
		if (!topic) return;
		return subscribeUpdates(topic, (event: CustomEvent<unknown>) => {
			handler(event);
		});
	}, [topic]);
}

export function useComponentExtensions<T = {}>(name: string, allowUnauthorized?: boolean) {
	useSubscribeTopic(componentExtensionTopic(name));

	// check the user is authorized
	const { authorized } = useIdentity(allowUnauthorized);
	const context = useContext(IdentityContext);

	if (!authorized && !allowUnauthorized) return [];

	return useExtensions<SecureExtension & { component: ComponentType<T> }>(`component-${name}`).map(a => a.component);
}

export function getComponentExtension<T>(name: string) {
	return getExtension<{ component: ComponentType<T> }>(`component-${name}`)?.component;
}

export const useExtensionsOptional = <T extends {}>(name?: string) => {
	const { getExtensions } = useExtensionContext();

	return name ? getExtensions<T>(name) : undefined;
};

/**
 * A type to indicate the categories where the extension should be included.
 */
export type ExtensionCategory = {
	// TODO: an operator and a name; the operator could be include/exclude, and the name could be the category.
	category?: string[];
};
/**
 * A type to enable an extension to be ordered automatically.
 */
export type ExtensionOrder = {
	order?: number;
};

/**
 * Function to get the extensions according to their names and categories.
 * @param name the name that the extensions were registered with.
 * @param category an array of categories that the extensions belong to.
 * @param includeUncategorised a boolean for whether the uncategorised extensions should be included i.e: those without a category array.
 * @returns if no categories are specified, the uncategorised extensions will be returned. Otherwise the relevant extensions are returned.
 */
export function getFilteredExtensions<T extends ExtensionCategory>(name: string, category: string[], includeUncategorised: boolean = true) {
	return getExtensions<T>(name).filter(e => {
		if (!e.category) return includeUncategorised;
		return category.some(c => e.category!.includes(c));
	});
}
/**
 * Function that retrieves the extensions appropriate to a category, and applies an order to them.
 * @param name the extension name
 * @param category the category to which the extension belongs eg 'email'
 * @param includeUncategorised a boolean that when true will return the uncategorised extensions (defaults to true)
 * @param defaultOrder number that will be used for sorting extensions that don't have an order defined; allowing you to position them at the end by default, or at the start by passing 0 to it (defaults to end-positioning)
 * @returns an array of extensions that are sorted according to the specified or default conditions
 */
export function getOrderedFilteredExtensions<T extends ExtensionOrder & ExtensionCategory>(
	name: string,
	category: string[],
	includeUncategorised: boolean = true,
	defaultOrder: number = 999999
) {
	return getFilteredExtensions<T>(name, category, includeUncategorised).sort((a, b) => (a.order ?? defaultOrder) - (b.order ?? defaultOrder));
}

export type SecureExtension = { requireFlags?: Array<string> };

/** type guard for feature required flags array */
function hasFeatureFlags(x: any): x is Required<SecureExtension> {
	if (Array.isArray(x?.requireFlags)) return true;
	return false;
}

declare var __DEV__: boolean;

export const useFilterExtensions = <T extends SecureExtension>(extensions: Array<T>) => {
	const flags = useAccessFlags();

	// the extensions dependency will have an identical identity, so we use the extensions length to determine whether we need to re-calculate the result
	// flags should have static identity once set also
	const availableExtensions = extensions.length;

	return useMemo(
		() =>
			extensions.filter(f => {
				if (hasFeatureFlags(f)) {
					// By default, in local dev mode we disregard permissions in terms of the activation
					// of routes / extension points for local dev convenience. If you actually need to
					// test the permission system in this context, comment out the last line and uncomment
					// the line below
					return f.requireFlags.every(rf => flags.has(rf));
					return __DEV__ || f.requireFlags!.every(rf => flags.has(rf));
				}
				return true;
			}),
		[extensions, availableExtensions, flags]
	);
};

export const useExtensions = <T extends {}>(name: string) => {
	const { getExtensions } = useExtensionContext();

	useSubscribeTopic(extensionTopic(name));

	return useFilterExtensions(getExtensions<T>(name));
};

function hashCode(str) {
	// java String#hashCode
	var hash = 0;
	for (var i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	return hash;
}

function intToRGB(i) {
	var c = (i & 0x00ffffff).toString(16).toUpperCase();

	return '00000'.substring(0, 6 - c.length) + c;
}

// export const useCompExt = useComponentExtensions;

// interface IProfileTabExtProps {
//     id: number
// }

// exmple usage
// const X = ({ id }: any) => <div><Extension<IProfileTabExtProps> name="person.profiletab" props={{ id }}>This is the fallback</Extension></div>

export function useExtensionPage(page: string) {
	const flags = useAccessFlags();
	return getExtensionPage(page, flags);
}
export function getExtensionPage(page: string, flags?: Set<string>): IRoute {
	if (!page) page = '/';

	for (const r of extensionContextStore.routeMatches) {
		if (typeof page === 'string' && r.slug === page && (!r.route.requireFlags || r.route.requireFlags.every(s => flags?.has(s) === true))) {
			return r.route;
		}
	}

	const defaultPage = extensionContextStore.routeMatches.filter(a => a.slug === '*')[0];
	if (defaultPage) return defaultPage.route;

	return { title: 'Error 404', PageComponent: PageNotFound, anonymous: false, childOnly: ChildOnlyOption.NoClick };
}
